import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mic,
  Square,
  Loader2,
  Check,
  AlertTriangle,
  Pencil,
  Pin,
  PinOff,

} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getLocalTodayYMD, formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";
import { useMicVolumeMeter } from "@/hooks/useMicVolumeMeter";
import MicVolumeMeter from "@/components/ai-post/MicVolumeMeter";
import { useVoicePostDirectLoad, type VoiceDirectFields } from "@/hooks/useVoicePostDirectLoad";
import { extractLaneFromTranscript } from "@/lib/lrDirect/voiceLaneFallback";
import { useVoicePostDefaults, type VoicePostDefaultKey } from "@/hooks/useVoicePostDefaults";

import { playAtlasResponse, stopAtlasResponse } from "@/lib/lrDirect/playAtlasResponse";
import { LRDirectAtlasCard, LRDirectAtlasCardHeader } from "./LRDirectAtlasCard";
import { VoiceExampleClipButton } from "./VoiceExampleClipButton";

/**
 * LR Direct ATLAS Voice Post — Part 1 (no audio confirmation), PURE VOICE.
 *
 * No buttons: post type (load/quote) and routing (private/open) are detected
 * from the spoken transcript, and are correctable as chips in the confirm step.
 * Flow mirrors the LaneRunner ATLAS Hub voice card: tap → recording + meter →
 * analyzing → verbatim transcript + parsed chips → Post / Start Over.
 * Reuses only the shared engine (voice-post edge function, mic meter).
 */

type VoiceState = "idle" | "recording" | "processing" | "done" | "error";
type PostType = "load" | "quote";
type Routing = "private" | "open";
type EditKey =
  | "type"
  | "routing"
  | "origin"
  | "destination"
  | "equipment"
  | "date"
  | "weight"
  | "rate";

const COMMON_EQUIPMENT = ["Reefer", "Dry Van", "Flatbed", "Step Deck", "Power Only", "Conestoga"];

/** Same accent token the manual LR Direct post form uses for quotes. */
const QUOTE_TOKEN = "--accent-quote";

function detectPostType(transcript: string): PostType | null {
  const t = transcript.toLowerCase();
  if (/\bquote (request|it)?\b|\brequest a quote\b|\bget quotes?\b|\bask for (a )?quote/.test(t)) return "quote";
  if (/\bpost (a |this )?load\b|\bpost it as a load\b/.test(t)) return "load";
  return null;
}

function detectRouting(transcript: string): Routing | null {
  if (/\bprivate network\b|\bmy network\b|\bmy brokers\b/i.test(transcript)) return "private";
  if (/\bopen market\b|\bpublic\b|\ball brokers\b/i.test(transcript)) return "open";
  return null;
}

function sanityWarnings(f: VoiceDirectFields): string[] {
  const w: string[] = [];
  if (f.weight_lbs && f.weight_lbs < 1000) w.push("Weight seems very low — did you mean a rate?");
  if (f.rate && f.rate < 100) w.push("Rate seems very low — did you mean weight?");
  if (f.weight_lbs && f.weight_lbs > 80000) w.push("Weight exceeds typical truck limits");
  return w;
}

export default function LRDirectVoicePostCard() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const postMutation = useVoicePostDirectLoad();
  const { defaults, loaded: defaultsLoaded, setDefault } = useVoicePostDefaults();
  /** Stable read of the latest defaults from inside memoized callbacks. */
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;


  // null until spoken or set in the confirm chips.
  const [postType, setPostType] = useState<PostType | null>(null);
  const [routing, setRouting] = useState<Routing | null>(null);
  const [state, setState] = useState<VoiceState>("idle");
  const [transcript, setTranscript] = useState("");
  const [fields, setFields] = useState<VoiceDirectFields>({});
  const [errorMsg, setErrorMsg] = useState("");
  const [editing, setEditing] = useState<EditKey | null>(null);
  const [editValue, setEditValue] = useState("");
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);
  /** Top strip turns orange only AFTER a quote has been posted successfully. */
  const [quotePosted, setQuotePosted] = useState(false);
  /** "My defaults" quick-editor popover (idle/done states). */
  const [defaultsOpen, setDefaultsOpen] = useState(false);


  /** Safety cap: recordings auto-stop and get processed after this long. */
  const MAX_RECORDING_SECONDS = 90;
  const [secondsLeft, setSecondsLeft] = useState(MAX_RECORDING_SECONDS);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const micLevel = useMicVolumeMeter(activeStream, state === "recording");

  const clearRecordingTimers = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const effective = useMemo<VoiceDirectFields>(
    () => ({ pickup_date: getLocalTodayYMD(), ...fields }),
    [fields],
  );
  const warnings = useMemo(() => (state === "done" ? sanityWarnings(effective) : []), [state, effective]);

  const canPost =
    !!effective.origin_city &&
    !!effective.destination_city &&
    !!effective.equipment_type &&
    !!postType &&
    !!routing;

  const reset = useCallback(() => {
    setState("idle");
    setTranscript("");
    setFields({});
    setErrorMsg("");
    setEditing(null);
    // Saved defaults pre-fill each new post; speech overrides them later.
    setPostType((defaultsRef.current.post_type as PostType) || null);
    setRouting((defaultsRef.current.routing as Routing) || null);
  }, []);

  // Once defaults land (or change), seed the idle chips so the user sees the
  // pre-filled values before they ever speak.
  useEffect(() => {
    if (!defaultsLoaded || state !== "idle") return;
    setPostType((prev) => prev ?? ((defaults.post_type as PostType) || null));
    setRouting((prev) => prev ?? ((defaults.routing as Routing) || null));
  }, [defaultsLoaded, defaults.post_type, defaults.routing, state]);

  const processAudio = useCallback(async (blob: Blob) => {
    try {
      setState("processing");
      const form = new FormData();
      form.append("audio", blob, blob.type.includes("mp4") ? "voice.m4a" : "voice.webm");
      form.append("mode", "load");
      // Stale-date fix: send the speaker's real local today + timezone so the
      // parser resolves relative dates ("tomorrow", "Monday") correctly.
      form.append("client_date", getLocalTodayYMD());
      form.append("client_tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "");

      const { data, error } = await supabase.functions.invoke("voice-post", { body: form });
      if (error) throw new Error(error.message || "Voice processing failed");
      if ((data as any)?.error) throw new Error((data as any).error);

      const said = ((data as any)?.transcript as string) || "";
      const parsed = ((data as any)?.fields as VoiceDirectFields) || {};
      setTranscript(said);

      // Speech ALWAYS wins. Saved defaults only fill what the transcript
      // didn't give us for this post.
      const saved = defaultsRef.current;
      const spokenType = detectPostType(said);
      const spokenRouting = detectRouting(said);
      const finalType = spokenType ?? ((saved.post_type as PostType) || null);
      const finalRouting = spokenRouting ?? ((saved.routing as Routing) || null);
      setPostType(finalType);
      setRouting(finalRouting);

      // Deterministic fallback: the model occasionally returns a null
      // origin/destination even when "<A> to <B>" was clearly spoken
      // (e.g. "McAllen TX to Bronx NY"). Fill only the missing side —
      // the chips stay editable before posting.
      const lane = extractLaneFromTranscript(said);
      const next: VoiceDirectFields = {
        ...parsed,
        pickup_date: parsed.pickup_date || getLocalTodayYMD(),
      };
      if (!next.equipment_type && saved.equipment_type) {
        next.equipment_type = saved.equipment_type;
      }
      if (!next.origin_city && lane.origin) {
        next.origin_city = lane.origin.city;
        next.origin_state = next.origin_state || lane.origin.state;
      }
      if (!next.destination_city && lane.destination) {
        next.destination_city = lane.destination.city;
        next.destination_state = next.destination_state || lane.destination.state;
      }
      if (next.origin_city && !next.origin_state && lane.origin?.city === next.origin_city) {
        next.origin_state = lane.origin.state;
      }
      if (
        next.destination_city &&
        !next.destination_state &&
        lane.destination?.city === next.destination_city
      ) {
        next.destination_state = lane.destination.state;
      }
      setFields(next);
      setState("done");

      // Incomplete parse → ATLAS speaks a "didn't catch that, try again" clip
      // ONCE per parse, in addition to the visible missing chips (unchanged).
      //
      // "Required" mirrors exactly what gates the Post button (canPost):
      // type, send-to (routing), origin, destination, equipment. Missing ANY
      // ONE of the five fires the clip. A chip filled by a SAVED DEFAULT is
      // not missing — this check runs on the post-default values.
      const missingRequired =
        !finalType ||
        !finalRouting ||
        !next.origin_city ||
        !next.destination_city ||
        !next.equipment_type;
      if (missingRequired) {
        playAtlasResponse(finalType === "quote" ? "quoteError" : "loadError");
      }
    } catch (e) {
      console.error("[LRDirectVoicePost] process failed", e);
      setErrorMsg(e instanceof Error ? e.message : "Could not process the recording");
      setState("error");
      // Transcribe/parse failure ("missing params / try again") — spoken response.
      playAtlasResponse(postType === "quote" ? "quoteError" : "loadError");
    }
  }, [postType]);


  const startRecording = useCallback(async () => {
    try {
      // No-overlap: a new recording stops any ATLAS clip still playing.
      stopAtlasResponse();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      cancelledRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        clearRecordingTimers();
        stream.getTracks().forEach((t) => t.stop());
        setActiveStream(null);
        if (cancelledRef.current) {
          cancelledRef.current = false;
          chunksRef.current = [];
          reset();
          return;
        }
        await processAudio(new Blob(chunksRef.current, { type: mimeType }));
      };

      recorder.start();
      recorderRef.current = recorder;
      setActiveStream(stream);
      setErrorMsg("");
      setState("recording");

      // Safety cap — auto-stop at 90s and process whatever was captured.
      clearRecordingTimers();
      setSecondsLeft(MAX_RECORDING_SECONDS);
      countdownRef.current = setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
      autoStopTimerRef.current = setTimeout(() => {
        if (recorderRef.current?.state === "recording") {
          cancelledRef.current = false;
          recorderRef.current.stop();
          setState("processing");
          // ATLAS spoken response for the 90s cap — never blocks processing.
          playAtlasResponse("timeout");
        }
      }, MAX_RECORDING_SECONDS * 1000);
    } catch (e) {
      console.error("Mic access error:", e);
      toast({
        variant: "destructive",
        title: "Microphone access denied",
        description: "Allow microphone access to use ATLAS Voice Post.",
      });
    }
  }, [processAudio, reset, toast, clearRecordingTimers]);

  const stopRecording = useCallback(() => {
    clearRecordingTimers();
    if (recorderRef.current?.state === "recording") {
      cancelledRef.current = false;
      recorderRef.current.stop();
      setState("processing");
    }
  }, [clearRecordingTimers]);

  // Release the mic if the card unmounts / user navigates away mid-recording.
  // NOTE: do NOT stop the ATLAS response clip here — a successful post navigates
  // away immediately, and stopping on unmount killed the clip ~1ms after start.
  // The player is a module-level singleton that intentionally outlives the card.
  useEffect(() => {
    return () => {
      clearRecordingTimers();
      const rec = recorderRef.current;
      if (rec && rec.state !== "inactive") {
        cancelledRef.current = true;
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      recorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
    };
  }, [clearRecordingTimers]);

  const openEdit = (key: EditKey) => {
    setEditing(key);
    const f = effective;
    setEditValue(
      key === "origin"
        ? [f.origin_city, f.origin_state].filter(Boolean).join(", ")
        : key === "destination"
        ? [f.destination_city, f.destination_state].filter(Boolean).join(", ")
        : key === "equipment"
        ? f.equipment_type || ""
        : key === "date"
        ? f.pickup_date || ""
        : key === "weight"
        ? f.weight_lbs != null
          ? String(f.weight_lbs)
          : ""
        : key === "rate"
        ? f.rate != null
          ? String(f.rate)
          : ""
        : "",
    );
  };

  const saveEdit = () => {
    const v = editValue.trim();
    setFields((prev) => {
      const next = { ...prev };
      if (editing === "origin" || editing === "destination") {
        const [cityPart, statePart] = v.split(",").map((s) => s.trim());
        if (editing === "origin") {
          next.origin_city = cityPart || null;
          next.origin_state = (statePart || "").toUpperCase() || null;
        } else {
          next.destination_city = cityPart || null;
          next.destination_state = (statePart || "").toUpperCase() || null;
        }
      } else if (editing === "equipment") {
        next.equipment_type = v || null;
      } else if (editing === "date") {
        next.pickup_date = /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : prev.pickup_date || getLocalTodayYMD();
      } else if (editing === "weight") {
        const n = Number(v.replace(/[^0-9.]/g, ""));
        next.weight_lbs = Number.isFinite(n) && n > 0 ? n : null;
      } else if (editing === "rate") {
        const n = Number(v.replace(/[^0-9.]/g, ""));
        next.rate = Number.isFinite(n) && n > 0 ? n : null;
      }
      return next;
    });
    setEditing(null);
  };

  const handlePost = async () => {
    if (!user?.id) {
      playAtlasResponse(postType === "quote" ? "quoteError" : "loadError");
      toast({ variant: "destructive", title: "Not signed in" });
      return;
    }
    // Missing required params (type / routing / lane) — this guard previously
    // returned silently with no spoken response at all.
    if (!postType || !routing || !canPost) {
      playAtlasResponse(postType === "quote" ? "quoteError" : "loadError");
      setErrorMsg("Missing details — tap a chip to fill the highlighted fields, then post.");
      return;
    }
    try {
      const res = await postMutation.mutateAsync({
        // Spoken (or chip-corrected) routing drives the private/open intent; the
        // hook still falls back to open market when the shipper has no network.
        fields: { ...effective, private_network: routing === "private" },
        postType,
        userId: user.id,
      });

      // PART 2: audio confirmation hook — ATLAS spoken response (non-blocking).
      playAtlasResponse(res.postType === "quote" ? "quoteSubmitted" : "loadPosted");

      if (res.postType === "quote") setQuotePosted(true);

      toast({
        title: res.postType === "quote" ? "Quote request posted" : "Load posted",
        description: `${res.lane} · pickup ${formatYYYYMMDDToLocaleDateString(res.pickupDate, undefined, {
          month: "short",
          day: "numeric",
        })} · ${res.visibility === "private_only" ? "private network" : "open market"}`,
      });
      reset();
      // Real shipper list routes (verified in the router), not the detail pages.
      navigate(res.postType === "quote" ? "/lr-direct/my-quotes" : "/lr-direct/my-loads");
    } catch (e) {
      // Silent-write guard: surface the real error, never a fake success.
      const msg = e instanceof Error ? e.message : "The post failed";
      setErrorMsg(msg);
      playAtlasResponse(postType === "quote" ? "quoteError" : "loadError");
      toast({ variant: "destructive", title: "Post failed", description: msg });
    }
  };

  // ── Saveable defaults (equipment / type / routing only) ──────────────────
  /** Which saved-default key a chip maps to, if any. */
  const defaultKeyFor = (key: EditKey): VoicePostDefaultKey | null =>
    key === "equipment" ? "equipment_type" : key === "type" ? "post_type" : key === "routing" ? "routing" : null;

  const savedDefaultFor = (key: EditKey): string | null => {
    const dk = defaultKeyFor(key);
    if (!dk) return null;
    return (defaults[dk] as string | null | undefined) || null;
  };

  const prettyDefault = (key: EditKey, raw: string): string =>
    key === "type"
      ? raw === "quote"
        ? "Quote request"
        : "Load"
      : key === "routing"
      ? raw === "private"
        ? "Private network"
        : "Open market"
      : raw;

  /** The chip's current value in the form the default is stored in. */
  const currentDefaultCandidate = (key: EditKey): string | null =>
    key === "equipment"
      ? (editing === "equipment" ? editValue.trim() : "") || effective.equipment_type || null

      : key === "type"
      ? postType
      : key === "routing"
      ? routing
      : null;

  const handleSaveDefault = async (key: EditKey) => {
    const dk = defaultKeyFor(key);
    const value = currentDefaultCandidate(key);
    if (!dk || !value) {
      toast({ variant: "destructive", title: "Pick a value first" });
      return;
    }
    const ok = await setDefault(dk, value);
    setEditing(null);
    toast(
      ok
        ? { title: "Default saved", description: `${prettyDefault(key, value)} will auto-fill every voice post.` }
        : { variant: "destructive", title: "Could not save default" },
    );
  };

  const handleClearDefault = async (key: EditKey) => {
    const dk = defaultKeyFor(key);
    if (!dk) return;
    const ok = await setDefault(dk, null);
    setEditing(null);
    toast(
      ok
        ? { title: "Default cleared", description: "You'll need to speak or set this value again." }
        : { variant: "destructive", title: "Could not clear default" },
    );
  };

  const defaultSummary = defaultsLoaded
    ? (["type", "routing", "equipment"] as EditKey[])
        .map((k) => {
          const v = savedDefaultFor(k);
          return v ? prettyDefault(k, v) : null;
        })
        .filter(Boolean)
    : [];


  const chips: { key: EditKey; label: string; value: string | null; required?: boolean }[] = [
    {
      key: "type",
      label: "Type",
      value: postType ? (postType === "quote" ? "Quote request" : "Load") : null,
      required: true,
    },
    {
      key: "routing",
      label: "Send to",
      value: routing ? (routing === "private" ? "Private network" : "Open market") : null,
      required: true,
    },
    {
      key: "origin",
      label: "Origin",
      value: effective.origin_city
        ? [effective.origin_city, effective.origin_state].filter(Boolean).join(", ")
        : null,
      required: true,
    },
    {
      key: "destination",
      label: "Destination",
      value: effective.destination_city
        ? [effective.destination_city, effective.destination_state].filter(Boolean).join(", ")
        : null,
      required: true,
    },
    { key: "equipment", label: "Equipment", value: effective.equipment_type || null, required: true },
    {
      key: "date",
      label: "Pickup",
      value: effective.pickup_date
        ? formatYYYYMMDDToLocaleDateString(effective.pickup_date, undefined, {
            month: "short",
            day: "numeric",
          })
        : null,
    },
    {
      key: "weight",
      label: "Weight",
      value: effective.weight_lbs ? `${effective.weight_lbs.toLocaleString()} lbs` : null,
    },
    { key: "rate", label: "Target rate", value: effective.rate ? `$${effective.rate.toLocaleString()}` : null },
  ];

  const headerBg = quotePosted ? `hsl(var(${QUOTE_TOKEN}))` : "#02ffd3";
  const headerFg = quotePosted ? "#ffffff" : "hsl(170 50% 15%)";
  const headerBorder = quotePosted ? `hsl(var(${QUOTE_TOKEN}) / 0.6)` : "hsl(170 60% 45% / 0.3)";

  return (
    <LRDirectAtlasCard className="flex flex-col">
      <LRDirectAtlasCardHeader
        icon={<Mic className="h-3.5 w-3.5" style={{ color: headerFg }} />}
        title="ATLAS Voice Post"
        bg={headerBg}
        fg={headerFg}
        borderColor={headerBorder}
      >
        <span
          className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{
            backgroundColor: quotePosted ? "#ffffff29" : "hsl(170 50% 15% / 0.12)",
            color: headerFg,
          }}
        >
          Live
        </span>
      </LRDirectAtlasCardHeader>

      <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-3">
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {state === "idle" &&
            "Say it all in one line — load or quote, private network or open market, then origin, destination, equipment."}
          {state === "recording" && "Listening... describe your post, then tap to stop."}
          {state === "processing" && "Analyzing and transcribing..."}
          {state === "done" && "Review below. Tap any chip to edit, then post."}
          {state === "error" && (errorMsg || "Something went wrong. Try again.")}
        </p>

        <div className="flex flex-col items-center gap-2 py-2">
          <div className="relative">
            {state === "recording" && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-full"
                style={{
                  border: "2px solid hsl(0 72% 51% / 0.5)",
                  animation: "ping 1.4s cubic-bezier(0,0,0.2,1) infinite",
                }}
              />
            )}
            <button
              type="button"
              onClick={
                state === "recording"
                  ? stopRecording
                  : state === "idle" || state === "error" || state === "done"
                  ? startRecording
                  : undefined
              }
              disabled={state === "processing"}
              aria-label={state === "recording" ? "Stop recording" : "Start recording"}
              className="relative h-14 w-14 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background:
                  state === "recording"
                    ? "hsl(0 72% 51%)"
                    : "linear-gradient(135deg, hsl(170 85% 32%), hsl(170 75% 42%))",
                boxShadow:
                  state === "recording"
                    ? "0 0 18px 4px hsl(0 72% 51% / 0.35)"
                    : "0 0 18px 4px hsl(170 85% 32% / 0.28)",
              }}
            >
              {state === "processing" ? (
                <Loader2 className="h-6 w-6 text-white animate-spin" />
              ) : state === "recording" ? (
                <Square className="h-5 w-5 text-white fill-white" />
              ) : (
                <Mic className="h-6 w-6 text-white" />
              )}
            </button>
          </div>
          {state === "recording" && <MicVolumeMeter level={micLevel} />}
          {state === "recording" && (
            <span
              className="text-[11px] font-semibold tabular-nums"
              style={{ color: secondsLeft <= 5 ? "hsl(0 72% 51%)" : undefined }}
            >
              0:{String(secondsLeft).padStart(2, "0")} left
              <span className="ml-1 font-normal text-muted-foreground">
                · auto-stops at {MAX_RECORDING_SECONDS}s
              </span>
            </span>
          )}
          <span className="text-[11px] text-muted-foreground italic">
            {state === "idle" && `Tap to start · auto-stops at ${MAX_RECORDING_SECONDS}s`}
            {state === "recording" && "Listening — tap to stop"}
            {state === "processing" && "Analyzing and transcribing..."}
            {state === "done" && "Complete"}
            {state === "error" && "Tap to retry"}
          </span>

          {state !== "recording" && state !== "processing" && (
            <VoiceExampleClipButton clip="loadPostingExample" label="Hear an example" />
          )}
        </div>

        {/* Verbatim transcript */}
        {(state === "processing" || state === "done") && (
          <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 text-[11px] text-foreground leading-relaxed min-h-[40px]">
            {state === "processing" ? (
              <span className="text-muted-foreground italic">Transcribing...</span>
            ) : (
              `"${transcript}"`
            )}
          </div>
        )}

        {state === "idle" && (
          <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 text-[11px] text-muted-foreground leading-relaxed italic">
            "Post a quote to my private network, Miami FL to Bronx NY, flatbed, 42k lbs Friday..."
          </div>
        )}

        {/* Saveable defaults guidance + what's currently pinned. */}
        {state !== "recording" && state !== "processing" && (
          <div className="space-y-1">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Set a default for chips you always use (like equipment) so you only speak what changes.
              Saved defaults auto-fill every post.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Popover open={defaultsOpen} onOpenChange={setDefaultsOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold hover:bg-muted"
                  >
                    <Pin className="h-2.5 w-2.5" /> My defaults
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2 space-y-2.5" align="start">
                  {(
                    [
                      {
                        dk: "post_type" as VoicePostDefaultKey,
                        label: "Type",
                        opts: [
                          { v: "load", label: "Load" },
                          { v: "quote", label: "Quote request" },
                        ],
                      },
                      {
                        dk: "routing" as VoicePostDefaultKey,
                        label: "Send to",
                        opts: [
                          { v: "private", label: "Private network" },
                          { v: "open", label: "Open market" },
                        ],
                      },
                      {
                        dk: "equipment_type" as VoicePostDefaultKey,
                        label: "Equipment",
                        opts: COMMON_EQUIPMENT.map((e) => ({ v: e, label: e })),
                      },
                    ]
                  ).map((group) => (
                    <div key={group.dk} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] uppercase tracking-wide text-muted-foreground">
                          {group.label}
                        </span>
                        {defaults[group.dk] && (
                          <button
                            type="button"
                            onClick={() => setDefault(group.dk, null)}
                            className="flex items-center gap-0.5 text-[9px] text-muted-foreground hover:text-foreground"
                          >
                            <PinOff className="h-2.5 w-2.5" /> Clear
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {group.opts.map((o) => (
                          <button
                            key={o.v}
                            type="button"
                            onClick={() => setDefault(group.dk, o.v)}
                            className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
                              defaults[group.dk] === o.v
                                ? "border-foreground bg-muted"
                                : "border-border hover:bg-muted"
                            }`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </PopoverContent>
              </Popover>
              {defaultSummary.length > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <span className="font-semibold">Your defaults:</span> {defaultSummary.join(" · ")}
                </span>
              )}
            </div>
          </div>
        )}



        {state === "done" && (
          <div className="space-y-2.5">
            <div className="flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <Popover
                  key={c.key}
                  open={editing === c.key}
                  onOpenChange={(o) => (o ? openEdit(c.key) : setEditing(null))}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={`group flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${
                        c.value
                          ? "border-border bg-muted/50"
                          : c.required
                          ? "border-destructive/50 bg-destructive/5 text-destructive"
                          : "border-dashed border-border text-muted-foreground"
                      }`}
                    >
                      <span className="uppercase tracking-wide text-[9px] opacity-70">{c.label}</span>
                      <span className="font-semibold">{c.value || (c.required ? "missing" : "—")}</span>
                      {savedDefaultFor(c.key) && (
                        <span
                          className="flex items-center gap-0.5 text-[9px] text-muted-foreground"
                          title={`Your saved default: ${prettyDefault(c.key, savedDefaultFor(c.key)!)}`}
                        >
                          <Pin className="h-2.5 w-2.5" />
                          default: {prettyDefault(c.key, savedDefaultFor(c.key)!)}
                        </span>
                      )}
                      <Pencil className="h-2.5 w-2.5 opacity-40 group-hover:opacity-100" />
                    </button>

                  </PopoverTrigger>
                  <PopoverContent className="w-60 p-2 space-y-2" align="start">
                    {c.key === "type" ? (
                      <div className="grid grid-cols-2 gap-1.5">
                        {(
                          [
                            { v: "load" as PostType, label: "Load" },
                            { v: "quote" as PostType, label: "Quote request" },
                          ]
                        ).map((o) => (
                          <button
                            key={o.v}
                            type="button"
                            onClick={() => {
                              setPostType(o.v);
                              setEditing(null);
                            }}
                            className={`rounded-md border px-2 py-1.5 text-[11px] font-semibold ${
                              postType === o.v ? "border-foreground" : "border-border hover:bg-muted"
                            }`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    ) : c.key === "routing" ? (
                      <div className="grid grid-cols-2 gap-1.5">
                        {(
                          [
                            { v: "private" as Routing, label: "Private network" },
                            { v: "open" as Routing, label: "Open market" },
                          ]
                        ).map((o) => (
                          <button
                            key={o.v}
                            type="button"
                            onClick={() => {
                              setRouting(o.v);
                              setEditing(null);
                            }}
                            className={`rounded-md border px-2 py-1.5 text-[11px] font-semibold ${
                              routing === o.v ? "border-foreground" : "border-border hover:bg-muted"
                            }`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <>
                        <Input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                          placeholder={
                            c.key === "origin" || c.key === "destination"
                              ? "City, ST"
                              : c.key === "date"
                              ? "YYYY-MM-DD"
                              : c.key === "equipment"
                              ? "Reefer"
                              : "Number"
                          }
                          className="h-8 text-xs"
                        />
                        {c.key === "equipment" && (
                          <div className="flex flex-wrap gap-1">
                            {COMMON_EQUIPMENT.map((eq) => (
                              <button
                                key={eq}
                                type="button"
                                onClick={() => setEditValue(eq)}
                                className="rounded border border-border px-1.5 py-0.5 text-[10px] hover:bg-muted"
                              >
                                {eq}
                              </button>
                            ))}
                          </div>
                        )}
                        <Button size="sm" className="h-7 w-full text-[11px]" onClick={saveEdit}>
                          <Check className="h-3 w-3 mr-1" /> Save
                        </Button>
                      </>
                    )}

                    {/* Saveable default — only the chips that repeat. */}
                    {defaultKeyFor(c.key) && (
                      <div className="border-t border-border pt-1.5 space-y-1">
                        <button
                          type="button"
                          onClick={() => handleSaveDefault(c.key)}
                          className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-[10px] font-semibold hover:bg-muted"
                        >
                          <Pin className="h-3 w-3" /> Save as my default
                        </button>
                        {savedDefaultFor(c.key) && (
                          <button
                            type="button"
                            onClick={() => handleClearDefault(c.key)}
                            className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-[10px] text-muted-foreground hover:bg-muted"
                          >
                            <PinOff className="h-3 w-3" /> Clear default (
                            {prettyDefault(c.key, savedDefaultFor(c.key)!)})
                          </button>
                        )}
                        <p className="px-1.5 text-[9px] text-muted-foreground leading-snug">
                          Defaults auto-fill every post. Anything you say overrides them.
                        </p>
                      </div>
                    )}
                  </PopoverContent>

                </Popover>
              ))}
            </div>

            {routing === "private" && (
              <p className="text-[10px] text-muted-foreground">
                Sending to your private network (falls back to open market if you have none).
              </p>
            )}

            {warnings.map((w) => (
              <div key={w} className="flex items-start gap-2 rounded-md bg-amber-50 px-2.5 py-1.5">
                <AlertTriangle className="h-3 w-3 text-amber-600 mt-0.5 shrink-0" />
                <span className="text-[11px] text-amber-800">{w}</span>
              </div>
            ))}

            {errorMsg && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5">
                <AlertTriangle className="h-3 w-3 text-destructive mt-0.5 shrink-0" />
                <span className="text-[11px] text-destructive">{errorMsg}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                className="flex-1 h-9 text-xs font-semibold text-white hover:opacity-90"
                disabled={!canPost || postMutation.isPending}
                onClick={handlePost}
                style={{ backgroundColor: "hsl(var(--brand-teal-deep))" }}
              >
                {postMutation.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Posting…
                  </>
                ) : postType === "quote" ? (
                  "Post Quote Request"
                ) : (
                  "Post Now"
                )}
              </Button>
              <Button variant="outline" className="h-9 text-xs" onClick={reset}>
                Start Over
              </Button>
            </div>
            {!canPost && (
              <p className="text-[10px] text-muted-foreground text-center">
                Type, routing, origin, destination and equipment are required before posting.
              </p>
            )}
          </div>
        )}
      </div>
    </LRDirectAtlasCard>
  );
}
